import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _, { capitalize } from 'lodash';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import { actions as kitActions } from '../Kits/redux/actions';
import { PageHeader } from '../../components/PageHeader';
import { TextInput } from '../../components/PageHeader/TextInput';
import Strings from '../../Strings';
import Select from '../../components/Select';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { CREATE_KIT, KITS_LIST_RESULT } from '../Kits/redux/constants';
import { DATE_MONTH_DAY_YEAR, DATE_MONTH_DAY_YEAR_WITH_DASH } from '../../constants';
import { openModalAction } from '../../actions/modal';
import { convertUnixEpochToHumanReadable } from '../../utils';
import { DateFilters } from '../../utils/DateFilters';
import { PERMISSIONS, hasPermission } from '../../utils/userPermissions';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest } from '../../components/AdvancedFilters/helpers';
import { isCapManager, isOrganizationManager, isShipper, isSuperUser } from '../../utils/userRoles';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import OrgSelect from '../../components/OrgSelect/OrgSelect';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../utils/tableUtils';

function Kits(props) {
  const { getKits, onNavigate, onCreateKit, filters, orgIds, orgs, history } = props;

  const [kits, setKits] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState(getSearchParamFromUrl() || '');

  const globalPageSize = useGlobalPageSize();
  const pageRequest = useRef({
    limit: globalPageSize,
    offset: 0,
    search: getSearchParamFromUrl(),
    sortColumn: 'updated_at',
    sortType: 'descending',
    orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
  });

  const loadingRef = useRef(false);
  const refreshEnabled = useRef(true);
  
  const load = (clearDataBeforeFetch = false) => {
    if (Number.isNaN(pageRequest.current.limit) || loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setIsLoading(true);
    if (clearDataBeforeFetch) {
      setKits([]);
      setPagination({});
    }
    getKits(pageRequest.current).then(resp => {
      loadingRef.current = false;
      if (resp.type === KITS_LIST_RESULT) {
        setKits(resp.response.kits || []);
        setPagination(resp.response.pagination || {});
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const prevOrgId = useRef(orgIds);
  const prevFilters = useRef(filters);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
      if (history?.location?.state?.params?.offset) {
        pageRequest.current.offset = history.location.state.params.offset;
      }
      pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;
      load();
      return;
    }

    // orgIds changed
    if (prevOrgId.current !== orgIds) {
      prevOrgId.current = orgIds;
      pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;
      pageRequest.current.offset = 0;
      load(true);
      return;
    }

    // filters changed
    if (prevFilters.current !== filters) {
      prevFilters.current = filters;
      const newFiltersForRequest = getFiltersForRequest(filters);
      if (JSON.stringify(newFiltersForRequest) !== JSON.stringify(pageRequest.current.filterBy)) {
        pageRequest.current.filterBy = newFiltersForRequest;
        pageRequest.current.offset = 0;
        load();
      }
    }
  }, [orgIds, filters]);

  const onRefresh = () => {
    if (refreshEnabled.current) {
      refreshEnabled.current = false;
      setTimeout(() => {
        refreshEnabled.current = true;
      }, 1000);
      pageRequest.current.offset = 0;
      load(true);
    }
  };

  const onOffsetChange = (offset, limit) => {
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    load();
  };

  const onPageSizeChange = limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    load(true);
  };

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    pageRequest.current.offset = 0;
    load(true);
  };

  const onRowClick = kit => {
    onNavigate(`kits/${kit.id}/devices`, { offset: pageRequest.current.offset });
  };

  const onSearchQueryChangeDebounced = useMemo(() => _.debounce(query => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    saveSearchParamToUrl(query);
    load(true);
  }, 1000), []);

  const onTextInputChange = e => {
    setSearchText(e.target.value);
    onSearchQueryChangeDebounced(e.target.value);
  };

  const onDateRangeChanged = dateRange => {
    if (dateRange) {
      pageRequest.current.startDate = dateRange.dates.startDate();
      pageRequest.current.endDate = dateRange.dates.endDate();
    } else {
      pageRequest.current.startDate = '';
      pageRequest.current.endDate = '';
    }
    pageRequest.current.offset = 0;
    load(true);
  };

  const columns = useMemo(() => {
    const cols = [
      columnHelper.accessor('id', {
        header: Strings.kitId,
        size: 250,
        enableSorting: false,
      }),
      columnHelper.accessor('name', {
        header: Strings.kitName,
        cell: info => info.getValue() || '-',
        size: 150,
      }),
    ];

    if (orgs?.length > 1) {
      cols.push(columnHelper.accessor('org_id', {
        header: Strings.organizationName,
        cell: info => {
          const orgId = info.getValue();
          return orgId && orgs ? orgs.find(o => o.masked_id === orgId)?.name || '-' : '-';
        },
        size: 180,

      }));
    }

    cols.push(
      columnHelper.accessor('usage_state', {
        header: Strings.usageState,
        cell: info => info.getValue() ? capitalize(info.getValue()) : '-',
        accessorFn: row => row.state?.usage_state,
        size: 120,
      }),
      columnHelper.accessor('administrative_state', {
        header: Strings.administrativeState,
        cell: info => info.getValue() ? capitalize(info.getValue()) : '-',
        accessorFn: row => row.state?.administrative_state,
        size: 150,
      }),
      columnHelper.accessor('operational_state', {
        header: Strings.operationalState,
        cell: info => info.getValue() ? capitalize(info.getValue()) : '-',
        accessorFn: row => row.state?.operational_state,
        size: 140,
      }),
      columnHelper.accessor('active_since', {
        header: Strings.activeSince,
        cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-',
        size: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('devices_count', {
        header: Strings.devices,
        cell: info => info.getValue() || '-',
        size: 80,
      }),
      columnHelper.accessor('devices_readings', {
        header: Strings.deviceReadings,
        cell: info => info.getValue() || '-',
        size: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('last_activity', {
        header: Strings.lastActivity,
        cell: info => info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-',
        size: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('updated_at', {
        header: Strings.updated_at,
        cell: info => info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-',
        size: 150,
      }),
      columnHelper.accessor('location', {
        header: Strings.location,
        cell: info => info.getValue() || '-',
        size: 150,
      }),
    );

    if (hasPermission(PERMISSIONS.DEVICES_KITS_SHOW_PATIENT)) {
      cols.push(columnHelper.accessor('pharmacy_patient_id', {
        header: Strings.capPatient.patientId,
        cell: info => info.getValue() || '-',
        size: 120,
        enableSorting: false,
      }));
    }

    return cols;
  }, [orgs]);

  const getHeaderComponents = () => {
    return (
      <>
        <OrgSelect />
        <Button class="refresh" onClick={onRefresh} />
        <TextInput className="search" placeholder={Strings.search} value={searchText} onChange={onTextInputChange} />
        <Select
          data={[
            DateFilters.AllTime,
            DateFilters.Last48Hours,
            DateFilters.ThisMonth,
            DateFilters.Last30Days,
            DateFilters.LastMonth,
            DateFilters.Last6Months,
            DateFilters.ThisYear,
            DateFilters.Last12Months,
          ]}
          onChange={onDateRangeChanged}
          defaultValue={DateFilters.AllTime.value}
        />
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().usage_state} />
          <Filter definition={getFiltersDefinition().administrative_state} />
          <Filter definition={getFiltersDefinition().operational_state} />
        </AdvancedFilters>
        {(isCapManager() || isOrganizationManager() || isShipper() || isSuperUser()) && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={() => onCreateKit(pageRequest.current, () => load())}
            title={Strings.createKit}
          />
        )}
      </>
    );
  };

  const headerTitle = !isLoading && pagination && kits
    ? Strings.formatString(
        Strings.showingXKits,
        kits?.length || '?',
        pagination?.totalRecords ? pagination.totalRecords : 0,
      )
    : Strings.showingWait;

  return (
    <div className="tab-page-content">
      <PageHeader isBlack left={headerTitle} right={getHeaderComponents()} />
      <AdvancedFiltersBar />
      <Table3
        name="kits"
        isLoading={isLoading}
        data={kits}
        columns={columns}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        onRowClick={onRowClick}
        enableColumnFiltering
        pinFirstColumn
      />
    </div>
  );
}

Kits.propTypes = {
  getKits: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  onCreateKit: PropTypes.func,
  history: PropTypes.object,
  filters: PropTypes.array,
  orgId: PropTypes.string,
  orgs: PropTypes.array,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
  orgIds: state.entities?.orgFilter?.orgIds,
  orgs: state.entities?.orgFilter?.orgs,
});

const mapDispatchToProps = dispatch => ({
  getKits: pageRequest => dispatch(kitActions.getKits(pageRequest)),
  onNavigate: (path, params) =>
    dispatch(
      push({
        pathname: path,
        state: { params },
      }),
    ),
  onCreateKit: (req, refreshFunction) =>
    dispatch(
      openModalAction('create-kit-and-add-devices', {
        action: kitActions.createKit,
        actionType: CREATE_KIT,
        onRefresh: refreshFunction,
        request: req,
      }),
    ),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Kits));
